import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { CircularProgress } from '@mui/material';
import { sa } from './index';
import { getSuperAdminHistory } from '../../services/api/identityUserService';
import { Timeline } from '../userManagement/userManagementStyle';
import { formatAuditTime, readAuditPage, targetSummary } from './auditFields';

const Heading = styled.h6`
  margin: 0 0 14px;
  font-family: ${sa.font.family};
  font-weight: ${sa.font.bold};
  font-size: 14px;
  color: ${sa.color.text};
`;
const StateText = styled.p`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  font-family: ${sa.font.family};
  font-size: 13px;
  color: ${(p) => (p.$error ? sa.color.pink : sa.color.textGray)};
`;

// The "Reveal History" panel for one super admin — the audit timeline scoped to activity
// performed BY that admin. Loads once per expansion; owns its own loading/empty/error so a
// history fetch never disturbs the surrounding super-admin list.
function SuperAdminHistory({ userId }) {
  const [state, setState] = useState({ status: 'loading', items: [], error: '' });

  useEffect(() => {
    let active = true;
    setState({ status: 'loading', items: [], error: '' });
    getSuperAdminHistory(userId)
      .then((body) => {
        if (!active) return;
        const { entries } = readAuditPage(body);
        setState({ status: 'loaded', items: entries, error: '' });
      })
      .catch((error) => {
        if (!active) return;
        setState({ status: 'error', items: [], error: error?.message || 'Could not load history.' });
      });
    return () => {
      active = false;
    };
  }, [userId]);

  return (
    <div className='super-admin-history'>
      <Heading>Activity History</Heading>

      {state.status === 'loading' && (
        <StateText>
          <CircularProgress size={16} sx={{ color: sa.color.teal }} />
          Loading history…
        </StateText>
      )}

      {state.status === 'error' && <StateText $error>{state.error}</StateText>}

      {state.status === 'loaded' && state.items.length === 0 && (
        <StateText>No activity recorded yet.</StateText>
      )}

      {state.status === 'loaded' && state.items.length > 0 && (
        <Timeline>
          {state.items.map((item) => {
            const meta = [targetSummary(item), item.summary, item.ipAddress]
              .filter((part) => part && part !== '—')
              .join(' · ');
            return (
              <li key={item.id}>
                <span className='event-title'>{item.actionLabel}</span>
                {meta && <span className='event-meta'>{meta}</span>}
                <span className='event-meta'>{formatAuditTime(item.occurredAt)}</span>
              </li>
            );
          })}
        </Timeline>
      )}
    </div>
  );
}

SuperAdminHistory.propTypes = {
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default SuperAdminHistory;
